import { Table, Button, Tag, Card, Statistic, Row, Col, Modal, Space, Tooltip, Tabs, Badge, Collapse } from "antd";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Header from "../../../Components/Header";
import { refreshicon, plusicon } from "../../../Components/imagepath";
import { itemRender, onShowSizeChange } from "../../../Components/Pagination";
import Sidebar from "../../../Components/Sidebar";

const { TabPane } = Tabs;
const { Panel } = Collapse;

const EventRegistrationsList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupedData, setGroupedData] = useState({});
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'all'
  const [stats, setStats] = useState({
    total: 0,
    registered: 0,
    confirmed: 0,
    cancelled: 0
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchData();
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [location, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "eventRegistrations"), orderBy("registrationDate", "desc"));
      const querySnapshot = await getDocs(q);
      
      const registrations = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        key: doc.id,
        ...doc.data(),
        registrationDate: doc.data().registrationDate?.toDate?.() || new Date(),
      }));

      setDataSource(registrations);

      // Group registrations by event
      const grouped = registrations.reduce((acc, registration) => {
        const eventTitle = registration.eventTitle || 'Unknown Event';
        const eventId = registration.eventId || 'unknown';
        
        if (!acc[eventTitle]) {
          acc[eventTitle] = {
            eventTitle,
            eventId,
            registrations: [],
            stats: {
              total: 0,
              registered: 0,
              confirmed: 0,
              cancelled: 0
            }
          };
        }
        
        acc[eventTitle].registrations.push(registration);
        acc[eventTitle].stats.total += 1;
        acc[eventTitle].stats[registration.status] = (acc[eventTitle].stats[registration.status] || 0) + 1;
        
        return acc;
      }, {});

      setGroupedData(grouped);

      // Calculate overall statistics
      const totalCount = registrations.length;
      const registeredCount = registrations.filter(reg => reg.status === 'registered').length;
      const confirmedCount = registrations.filter(reg => reg.status === 'confirmed').length;
      const cancelledCount = registrations.filter(reg => reg.status === 'cancelled').length;

      setStats({
        total: totalCount,
        registered: registeredCount,
        confirmed: confirmedCount,
        cancelled: cancelledCount
      });

    } catch (error) {
      console.error("Error fetching registrations:", error);
      toast.error("Error loading registrations");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, "eventRegistrations", id), {
        status: newStatus,
        updatedAt: new Date()
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    
    try {
      setDeleting(true);
      await deleteDoc(doc(db, "eventRegistrations", selectedRecord.id));
      toast.success("Registration deleted successfully");
      setDeleteModalVisible(false);
      setSelectedRecord(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting registration:", error);
      toast.error("Error deleting registration");
    } finally {
      setDeleting(false);
    }
  };

  const showDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const showDeleteConfirm = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'registered': return 'blue';
      case 'confirmed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const renderGroupedView = () => {
    const eventGroups = Object.values(groupedData);
    
    return (
      <Collapse 
        size="large" 
        defaultActiveKey={eventGroups.length > 0 ? [eventGroups[0].eventTitle] : []}
      >
        {eventGroups.map((eventGroup) => (
          <Panel
            key={eventGroup.eventTitle}
            header={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{eventGroup.eventTitle}</strong>
                  <span style={{ marginLeft: '16px', color: '#666' }}>
                    ({eventGroup.stats.total} registrations)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Badge count={eventGroup.stats.registered} style={{ backgroundColor: '#52c41a' }} />
                  <Badge count={eventGroup.stats.confirmed} style={{ backgroundColor: '#13c2c2' }} />
                  <Badge count={eventGroup.stats.cancelled} style={{ backgroundColor: '#ff4d4f' }} />
                </div>
              </div>
            }
          >
            <Table
              columns={columnsForEvent}
              dataSource={eventGroup.registrations}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} registrations for this event`,
              }}
              size="small"
            />
          </Panel>
        ))}
      </Collapse>
    );
  };

  const columnsForEvent = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: (email) => (
        <span className="font-medium text-gray-800">{email}</span>
      )
    },
    {
      title: "Adults",
      dataIndex: "numberOfAdults",
      key: "numberOfAdults",
      width: 80,
      sorter: (a, b) => a.numberOfAdults - b.numberOfAdults,
      render: (count) => (
        <Tag color="blue">{count}</Tag>
      )
    },
    {
      title: "Children",
      dataIndex: "numberOfChildren",
      key: "numberOfChildren",
      width: 80,
      sorter: (a, b) => a.numberOfChildren - b.numberOfChildren,
      render: (count) => (
        <Tag color="green">{count}</Tag>
      )
    },
    {
      title: "Adult Names",
      dataIndex: "adultNames",
      key: "adultNames",
      render: (names) => (
        <Tooltip title={names}>
          <span className="text-sm">
            {names.length > 30 ? `${names.substring(0, 30)}...` : names}
          </span>
        </Tooltip>
      )
    },
    {
      title: "Registration Date",
      dataIndex: "registrationDate",
      key: "registrationDate",
      sorter: (a, b) => new Date(a.registrationDate) - new Date(b.registrationDate),
      render: (date) => (
        <span className="text-sm text-gray-600">
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </span>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Tag 
          color={getStatusColor(status)}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            const nextStatus = status === 'registered' ? 'confirmed' : 
                              status === 'confirmed' ? 'cancelled' : 'registered';
            handleStatusChange(record.id, nextStatus);
          }}
        >
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<FeatherIcon icon="eye" size={14} />}
            onClick={() => showDetail(record)}
          />
          <Button
            type="primary"
            danger
            size="small"
            icon={<FeatherIcon icon="trash-2" size={14} />}
            onClick={() => showDeleteConfirm(record)}
          />
        </Space>
      )
    }
  ];

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: (email) => (
        <span className="font-medium text-gray-800">{email}</span>
      )
    },
    {
      title: "Event",
      dataIndex: "eventTitle",
      key: "eventTitle",
      sorter: (a, b) => (a.eventTitle || '').localeCompare(b.eventTitle || ''),
      render: (eventTitle) => (
        <Tooltip title={eventTitle}>
          <span className="text-sm font-medium">
            {eventTitle ? (eventTitle.length > 25 ? `${eventTitle.substring(0, 25)}...` : eventTitle) : 'Unknown Event'}
          </span>
        </Tooltip>
      )
    },
    {
      title: "Adults",
      dataIndex: "numberOfAdults",
      key: "numberOfAdults",
      width: 80,
      sorter: (a, b) => a.numberOfAdults - b.numberOfAdults,
      render: (count) => (
        <Tag color="blue">{count}</Tag>
      )
    },
    {
      title: "Children",
      dataIndex: "numberOfChildren",
      key: "numberOfChildren",
      width: 80,
      sorter: (a, b) => a.numberOfChildren - b.numberOfChildren,
      render: (count) => (
        <Tag color="green">{count}</Tag>
      )
    },
    {
      title: "Adult Names",
      dataIndex: "adultNames",
      key: "adultNames",
      render: (names) => (
        <Tooltip title={names}>
          <span className="text-sm">
            {names.length > 30 ? `${names.substring(0, 30)}...` : names}
          </span>
        </Tooltip>
      )
    },
    {
      title: "Registration Date",
      dataIndex: "registrationDate",
      key: "registrationDate",
      sorter: (a, b) => new Date(a.registrationDate) - new Date(b.registrationDate),
      render: (date) => (
        <span className="text-sm text-gray-600">
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </span>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: 'Registered', value: 'registered' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <Tag 
          color={getStatusColor(status)}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            const nextStatus = status === 'registered' ? 'confirmed' : 
                              status === 'confirmed' ? 'cancelled' : 'registered';
            handleStatusChange(record.id, nextStatus);
          }}
        >
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<FeatherIcon icon="eye" size={14} />}
            onClick={() => showDetail(record)}
          />
          <Button
            type="primary"
            danger
            size="small"
            icon={<FeatherIcon icon="trash-2" size={14} />}
            onClick={() => showDeleteConfirm(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar 
        id="menu-item4"
        id1="menu-items4"
        activeClassName="event-registrations"
      />
      
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Event Registrations</h4>
              <h6>Manage event registrations and participant information</h6>
            </div>
            <div className="page-btn">
              <Space>
                <Button
                  type={viewMode === 'grouped' ? 'primary' : 'default'}
                  onClick={() => setViewMode('grouped')}
                >
                  <FeatherIcon icon="layers" size={14} />
                  By Events
                </Button>
                <Button
                  type={viewMode === 'all' ? 'primary' : 'default'}
                  onClick={() => setViewMode('all')}
                >
                  <FeatherIcon icon="list" size={14} />
                  All Registrations
                </Button>
                <Button
                  type="primary"
                  icon={<img src={refreshicon} alt="refresh" />}
                  onClick={fetchData}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>
          </div>

          {/* Statistics Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Registrations"
                  value={stats.total}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Registered"
                  value={stats.registered}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Confirmed"
                  value={stats.confirmed}
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Cancelled"
                  value={stats.cancelled}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Content based on view mode */}
          {viewMode === 'grouped' ? (
            <div className="card">
              <div className="card-body">
                <h5 style={{ marginBottom: '16px' }}>Registrations by Event</h5>
                {Object.keys(groupedData).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <FeatherIcon icon="calendar" size={48} />
                    <p style={{ marginTop: '16px' }}>No event registrations found</p>
                  </div>
                ) : (
                  renderGroupedView()
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <h5 style={{ marginBottom: '16px' }}>All Registrations</h5>
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                      total: dataSource.length,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} registrations`,
                      onShowSizeChange: onShowSizeChange,
                      itemRender: itemRender,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        title="Registration Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedRecord && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Email:</strong>
                <p>{selectedRecord.email}</p>
              </div>
              <div>
                <strong>Event:</strong>
                <p>{selectedRecord.eventTitle}</p>
              </div>
              <div>
                <strong>Number of Adults:</strong>
                <p>{selectedRecord.numberOfAdults}</p>
              </div>
              <div>
                <strong>Number of Children:</strong>
                <p>{selectedRecord.numberOfChildren}</p>
              </div>
            </div>
            
            <div>
              <strong>Adult Names:</strong>
              <p>{selectedRecord.adultNames}</p>
            </div>
            
            {selectedRecord.numberOfChildren > 0 && (
              <div>
                <strong>Children Names & Ages:</strong>
                <p>{selectedRecord.childrenNames}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Adult Swahili Level:</strong>
                <p>{selectedRecord.adultSwahiliLevel}</p>
              </div>
              {selectedRecord.childSwahiliLevel && (
                <div>
                  <strong>Child Swahili Level:</strong>
                  <p>{selectedRecord.childSwahiliLevel}</p>
                </div>
              )}
            </div>
            
            {selectedRecord.specialRequests && (
              <div>
                <strong>Special Requests:</strong>
                <p>{selectedRecord.specialRequests}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Registration Date:</strong>
                <p>{selectedRecord.registrationDate.toLocaleString()}</p>
              </div>
              <div>
                <strong>Status:</strong>
                <Tag color={getStatusColor(selectedRecord.status)}>
                  {selectedRecord.status.toUpperCase()}
                </Tag>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        confirmLoading={deleting}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure you want to delete this registration?</p>
        {selectedRecord && (
          <p><strong>Email:</strong> {selectedRecord.email}</p>
        )}
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default EventRegistrationsList;